import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { Text } from 'components/Layout';

import { getColorClassName, COLOR } from 'utilsTS/color';
import { ref } from 'utilsTS/prop-types';

import { CardBody } from './CardBody';

export enum CARD_PADDING {
    NONE = 'none',
    MEDIUM = 'medium',
    LARGE = 'large',
}

export interface CardProps extends React.ComponentPropsWithoutRef<'div'> {
    innerRef?: React.Ref<HTMLDivElement>;
    /** Defines background colour of the Card */
    color?: COLOR;
    /** Defines whether padding should be rendered
     * @deprecated use padding={Card.PADDING.NONE} instead
     */
    padded?: boolean;
    /** Defines padding size */
    padding?: CARD_PADDING;
    /** Defines whether the component should be sticky at the top of the viewport */
    sticky?: boolean;
    /** Defines the offset from the top of the viewport */
    stickyOffset?: string | number;
    /** Defines whether an additional body wrapper should be rendered */
    useCardBody?: boolean;
    /** Defines whether Headings should all have the same size inside Card */
    restrictHeadingSize?: boolean;
}

export interface CardStatics {
    COLOR: typeof COLOR;
    PADDING: typeof CARD_PADDING;
}

export const Card: React.FC<CardProps> & CardStatics = ({
    innerRef,
    color,
    padded = true,
    padding,
    sticky = false,
    stickyOffset = 0,
    useCardBody = true,
    restrictHeadingSize = true,
    className,
    children,
    style,
    ...rest
}) => {
    const classNames = cx(
        'card',
        'g-card',
        getColorClassName(color),
        { 'g-card-no-padding': !padded || padding === CARD_PADDING.NONE },
        { 'g-card-large': padding === CARD_PADDING.LARGE },
        { 'g-card-sticky': !!sticky },
        restrictHeadingSize && 'g-uniform-headings',
        className,
    );

    const content = <Text>{children}</Text>;

    return (
        <div
            ref={innerRef}
            className={classNames}
            style={{ top: sticky ? stickyOffset : undefined, ...style }}
            {...rest}
        >
            {children && (useCardBody ? <CardBody>{content}</CardBody> : content)}
        </div>
    );
};

Card.COLOR = COLOR;
Card.PADDING = CARD_PADDING;

Card.displayName = 'Card';
Card.propTypes = {
    innerRef: ref(),
    color: PropTypes.oneOf(Object.values(Card.COLOR)),
    padded: PropTypes.bool,
    padding: PropTypes.oneOf(Object.values(Card.PADDING)),
    sticky: PropTypes.bool,
    stickyOffset: PropTypes.number,
    useCardBody: PropTypes.bool,
    restrictHeadingSize: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
    style: PropTypes.shape({}),
};
